<template>
  <table class="tw-table-fixed" :class="{ 'plain-list': plain }">
    <thead v-if="!plain">
      <tr class="tw-bg-true-gray-50 tw-rounded tw-text-gray-600">
        <th class="tw-font-normal">{{ $t('Field') }}</th>
        <th class="tw-font-normal">{{ $t('Value') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="customFieldValue in customFieldsData"
        :key="customFieldValue.id"
        class="tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100"
          :class="{'tw-text-gray-700': customFieldValue.customField.archivedAt !== null }"
      >
        <td class="tw-w-1/2">
          {{ customFieldValue.customField.title }}
          <WdsLabel 
          v-if="customFieldValue.customField.archivedAt !== null"
          class="tw-ml-1"
          subtle 
          xs 
          gray>
            {{$t('Archived')}}
          </WdsLabel>
          <WdsTag
            v-if="optionalFieldIds.has(customFieldValue.customField.id)"
            sm
            class="tw-ml-1"
            data-test="custom-field-optional"
          >
            {{ $t('Optional') }}
          </WdsTag>
          <CustomFieldVisibilityBadge
            :applies-to="customFieldValue.customField.appliesTo"
            :shared-with-clients="customFieldValue.customField.sharedWithClients"
            :shared-with-staffing-agencies="customFieldValue.customField.sharedWithStaffingAgencies"
            :managed-elsewhere="customFieldValue.customField.managedElsewhere"
            :owner-account-name="customFieldValue.customField.ownerAccountName"
          />
        </td>
        <td class="tw-w-1/2">
          <ul
            v-if="customFieldValue.files.length > 0"
            class="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-1"
          >
            <li v-for="file in customFieldValue.files" :key="file.id">
              <a
                :href="file.url"
                target="_blank"
                rel="noopener noreferrer"
                class="tw-text-blue-600 tw-underline"
              >
                {{ file.name }}
              </a>
            </li>
          </ul>
          <template v-else-if="customFieldValue.fieldType === CustomFieldType.FileUpload">
            <span class="tw-text-gray-500">{{ $t('No file attached') }}</span>
          </template>
          <WdsText v-else-if="customFieldValue.isEmpty" secondary>
            {{ plain ? $t('Not answered') : $t('Not specified') }}
          </WdsText>
          <template v-else>{{ customFieldValue.displayValue }}</template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsLabel, WdsTag, WdsText } from '@worksome/wds-components'
import { FragmentType, useFragment } from '@/gql'
import {
  CUSTOM_FIELD_VALUES_VIEW_FRAGMENT,
  FieldableFormCustomFieldFragment,
} from '@/components/Fieldable/graphql/fragments'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { CustomFieldType, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'

interface DisplayFile {
  id: string
  name: string
  url: string
}

interface DisplayRow {
  id: string
  displayValue: string | null | undefined
  isEmpty?: boolean
  fieldType: CustomFieldType
  files: DisplayFile[]
  customField: {
    id: string
    title: string
    description: string | null | undefined
    appliesTo: TypeSupportingCustomFieldValues
    archivedAt: string | null
    sharedWithClients: boolean
    sharedWithStaffingAgencies: boolean
    managedElsewhere: boolean
    ownerAccountName: string | null
  }
}

const props = defineProps<{
  customFieldValues: FragmentType<typeof CUSTOM_FIELD_VALUES_VIEW_FRAGMENT>[]
  // When given, every one of these fields gets a row, in this order, even without a value.
  fields?: FragmentType<typeof FieldableFormCustomFieldFragment>[]
  // A borderless list without the Field/Value header, for grouped sections.
  plain?: boolean
}>()

// Like the forms, only optional fields are labelled; known only when the field list is passed.
const optionalFieldIds = computed(
  () =>
    new Set(
      (props.fields ?? [])
        .map((field) => useFragment(FieldableFormCustomFieldFragment, field))
        .filter((field) => !isCustomFieldRequired(field.settings))
        .map((field) => field.id),
    ),
)

const customFieldsData = computed<DisplayRow[]>(() => {
  const typed = props.customFieldValues.map((customFieldValue) =>
    useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, customFieldValue),
  )

  const grouped = new Map<string, DisplayRow>()
  for (const cv of typed) {
    const key = cv.customField.id
    const { fieldType } = cv.customField
    const isFileUpload = fieldType === CustomFieldType.FileUpload
    const file: DisplayFile | null = cv.file
      ? { id: cv.file.id, name: cv.file.name, url: cv.file.url }
      : null
    if (grouped.has(key)) {
      const existing = grouped.get(key)!
      // For non-FileUpload fields, accumulate displayValue strings (skipping
      // empty parts so we never render literal "null" or a stray comma).
      // FileUpload renders the `files` list directly — no need to compute
      // a comma-joined filename string that the template never reads.
      if (!isFileUpload) {
        const parts = [existing.displayValue, cv.displayValue].filter((part): part is string =>
          Boolean(part),
        )
        existing.displayValue = parts.length > 0 ? parts.join(', ') : null
      }
      if (file) {
        existing.files.push(file)
      }
    } else {
      grouped.set(key, {
        id: cv.id,
        displayValue: isFileUpload ? null : cv.displayValue,
        fieldType,
        files: file ? [file] : [],
        customField: {
          id: cv.customField.id,
          title: cv.customField.title,
          description: cv.customField.description ?? null,
          appliesTo: cv.customField.appliesTo,
          archivedAt: cv.customField.archivedAt,
          sharedWithClients: cv.customField.sharedWithClients,
          sharedWithStaffingAgencies: cv.customField.sharedWithStaffingAgencies,
          managedElsewhere: !cv.customField.viewerOwnsField,
          ownerAccountName: cv.customField.account?.name ?? null,
        },
      })
    }
  }
  const archivedLast = (rows: DisplayRow[]) =>
    rows.sort(
      (a, b) => Number(a.customField.archivedAt !== null) - Number(b.customField.archivedAt !== null),
    )

  if (!props.fields) {
    return archivedLast(Array.from(grouped.values()))
  }

  const fieldRows = props.fields.map((fieldFragment) => {
    const field = useFragment(FieldableFormCustomFieldFragment, fieldFragment)

    return (
      grouped.get(field.id) ?? {
        id: field.id,
        displayValue: null,
        isEmpty: true,
        fieldType: field.fieldType,
        files: [],
        customField: {
          id: field.id,
          title: field.title,
          description: field.description ?? null,
          appliesTo: field.appliesTo,
          archivedAt: null,
          sharedWithClients: field.sharedWithClients,
          sharedWithStaffingAgencies: field.sharedWithStaffingAgencies,
          managedElsewhere: !field.viewerOwnsField,
          ownerAccountName: field.account?.name ?? null,
        },
      }
    )
  })
  const listedIds = new Set(fieldRows.map((row) => row.customField.id))

  return archivedLast([
    ...fieldRows,
    ...Array.from(grouped.values()).filter((row) => !listedIds.has(row.customField.id)),
  ])
})
</script>

<style scoped lang="scss">
td,
th {
  @apply tw-py-1;
  @apply tw-px-2;
}

.plain-list td {
  @apply tw-py-3;
}

thead > tr > th {
  &:first-child {
    @apply tw-rounded-tl-lg;
    @apply tw-rounded-bl-lg;
  }
  &:last-child {
    @apply tw-rounded-tr-lg;
    @apply tw-rounded-br-lg;
  }
}
</style>
